import { useState } from 'react';
import { Link } from '../../router/Link';
import { useNavigate } from '../../router/router';
import { Button, LoadingStatus, Menu, Notice, ProblemPanel, Spinner, StatePanel } from '../../ui';
import { FeedGridSkeleton, ListingGrid } from '../../listing/FeedGrid';
import { useAutoLoadMore } from '../../listing/useAutoLoadMore';
import { menuItemHref, type FilterMenuId } from './model';
import type { SearchBinding } from './useSearch';
import styles from './search.module.css';
import { useCopy } from '../../strings/useCopy';

/**
 * §8's Search page. Renders props (R13) — the branch it takes is `state.kind`, every string arrived
 * formatted and every href arrived built.
 *
 * **The filter bar is outside the state switch on purpose.** A search that failed, returned nothing, or
 * paged past its end still shows its own controls, because in every one of those cases the useful next
 * action is changing a filter. Hiding them behind a successful response is the version of this page that
 * traps the reader.
 *
 * The controls are buttons that navigate rather than anchors, and that is the one place this page
 * departs from the app's "a link is a real `<a href>`" rule. A filter is a *toggle over the current
 * page* — `aria-pressed` and `menuitemradio` are what a screen reader needs to hear — and the URL still
 * changes, so back, forward and copying the address all behave. The hrefs are still built in `model.ts`;
 * only the element is different.
 */
export function Search({ binding }: { readonly binding: SearchBinding }) {
  const words = useCopy().text.search.page;
  const navigate = useNavigate();
  const { controls, state, query } = binding;
  const [openMenu, setOpenMenu] = useState<FilterMenuId | null>(null);
  const autoLoad = useAutoLoadMore(
    state.kind === 'ready' && state.more && !binding.loadingMore,
    binding.loadMore,
  );

  const renderMenu = (filter: (typeof controls.menus)[number]) => (
    <Menu
      key={filter.id}
      menu={filter.menu}
      multiSelect={filter.multiSelect}
      disabled={filter.disabled}
      title={filter.title ?? undefined}
      className={filter.id === 'sort' ? styles.sortMenu : undefined}
      open={openMenu === filter.id}
      onOpenChange={(next) => setOpenMenu(next ? filter.id : null)}
      onSelect={(itemId) => {
        // A multi-select menu stays open — picking two features is one gesture, and closing
        // after the first would make the second a second trip.
        if (!filter.multiSelect) setOpenMenu(null);
        navigate(menuItemHref(query, filter.id, itemId));
      }}
    />
  );

  return (
    <section className={styles.search} aria-busy={state.kind === 'loading' ? true : undefined}>
      {/* The other screens' header shape — one heading, no lede. The query keeps its own span only
          for `overflow-wrap`: a 200-character term has to wrap rather than widen the page. */}
      <header className={styles.head}>
        <h1 className={styles.heading}>
          <span className={styles.term}>
            {words.resultsFor} “{query.q}”
          </span>
          {binding.refreshing ? (
            <span className={styles.refreshing}>
              <Spinner />
            </span>
          ) : null}
        </h1>
      </header>

      <div className={styles.filters} role="group" aria-label={words.filtersLabel}>
        {/* Sort renders last and takes the row's right edge — the reset stays with the
            filter cluster it clears, so the two right-hand things do not read as one control. */}
        <div className={styles.menus}>
          {controls.menus.filter((filter) => filter.id !== 'sort').map(renderMenu)}

          {controls.activeFilters === 0 ? null : (
            <Link className={styles.reset} href={controls.resetHref}>
              {words.clearFilterCount(controls.activeFilters)}
            </Link>
          )}

          {controls.menus.filter((filter) => filter.id === 'sort').map(renderMenu)}
        </div>
      </div>

      {state.kind === 'loading' ? (
        <>
          <LoadingStatus>{words.searching(query.q)}</LoadingStatus>
          <FeedGridSkeleton cards={state.skeletonCards} />
        </>
      ) : null}

      {state.kind === 'problem' ? <ProblemPanel copy={state.copy} onRetry={binding.retry} /> : null}

      {state.kind === 'exhausted' ? (
        <StatePanel
          title={words.exhausted}
          detail={words.exhaustedDetail(state.page)}
        >
          <Link className={styles.pageLink} href={state.previousHref}>
            {words.backToPage(state.page - 1)}
          </Link>
        </StatePanel>
      ) : null}

      {state.kind === 'empty' ? (
        <>
          {state.notices.map((notice) => (
            <Notice key={notice.id} tone="caution">
              {notice.text}
              {notice.action === null ? null : (
                <>
                  {' '}
                  <Link className={styles.noticeLink} href={notice.action.href}>
                    {notice.action.label}
                  </Link>
                </>
              )}
            </Notice>
          ))}
          <StatePanel title={state.title} detail={state.detail}>
            {state.resetHref === null ? null : (
              <Link className={styles.pageLink} href={state.resetHref}>
                {words.clearFilters}
              </Link>
            )}
          </StatePanel>
        </>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          {state.notices.map((notice) => (
            <Notice key={notice.id} tone="caution">
              {notice.text}
              {notice.action === null ? null : (
                <>
                  {' '}
                  <Link className={styles.noticeLink} href={notice.action.href}>
                    {notice.action.label}
                  </Link>
                </>
              )}
            </Notice>
          ))}

          <ListingGrid rows={state.rows} label={words.gridLabel(query.q)} />

          <div className={styles.paging} ref={autoLoad}>
            {state.more ? (
              <Button onClick={binding.loadMore} disabled={binding.loadingMore}>
                {binding.loadingMore ? words.loadingMore : words.loadMore(state.rows.length)}
              </Button>
            ) : (
              // Stated rather than left as a dead button — and it is a real proof: the instance refused
              // the next page, or answered it empty (`model.ts`'s `more`).
              <p className={styles.pagingDone}>{words.allLoaded(state.rows.length)}</p>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}
